import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, Button, Stack, Chip, CircularProgress,
    FormControl, Select, MenuItem, LinearProgress, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert
} from '@mui/material';
import {
    AccountBalanceWallet as PayrollIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    PeopleAlt as HeadcountIcon,
    Business as DeptIcon,
    ReceiptLong as PayslipIcon,
    CheckCircle as CheckCircleIcon,
    PlayArrow as RunIcon,
    Assessment as ReviewIcon,
    Refresh as RefreshIcon,
    ShieldOutlined as TaxIcon,
    Apartment as CostCentreIcon,
    ArrowForward as ArrowForwardIcon,
    LockOutlined as LockIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
    CartesianGrid, Tooltip as ChartTooltip, Legend
} from 'recharts';
import AppHeader from '../../components/AppHeader';

const C = {
    primary: '#4338ca',
    primaryLight: '#eef2ff',
    secondary: '#0284c7',
    emerald: '#10b981',
    emeraldLight: '#ecfdf5',
    rose: '#f43f5e',
    roseLight: '#fff1f2',
    amber: '#f59e0b',
    amberLight: '#fffbeb',
    slateDark: '#0f172a',
    slateMid: '#475569',
    slateLight: '#64748b',
    border: '#e2e8f0',
    cardBg: '#ffffff',
    bg: '#f8fafc'
};

const money = (val) => Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
});

const formatCompact = (val) => {
    const num = Number(val || 0);
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(0) + 'K';
    }
    return String(num);
};

const getStatusChipProps = (status) => {
    switch (status) {
        case 'FINALIZED':
            return { label: 'Finalized', color: 'success', bg: '#ecfdf5', text: '#065f46' };
        case 'APPROVED':
            return { label: 'Approved', color: 'info', bg: '#eff6ff', text: '#1e40af' };
        case 'SUBMITTED':
            return { label: 'Submitted', color: 'warning', bg: '#fffbeb', text: '#92400e' };
        case 'DRAFT':
            return { label: 'Draft', color: 'default', bg: '#f1f5f9', text: '#334155' };
        case 'VOID':
            return { label: 'Void', color: 'error', bg: '#fff1f2', text: '#9f1239' };
        default:
            return { label: status || 'Unknown', color: 'default', bg: '#f1f5f9', text: '#334155' };
    }
};

const PayrollDashboard = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const tenant = localStorage.getItem('tenant') || '';

    // Security & Access Management evaluation
    const storedUser = sessionStorage.getItem("user");
    const userObj = storedUser ? JSON.parse(storedUser) : null;
    const userRole = (userObj?.role || '').toLowerCase();
    const userPermissions = userObj?.permissions || [];

    const isSuperAdmin = userRole === 'super_admin' || userRole === 'support_admin';
    const isHrManager = userRole === 'hr_manager';
    const isManagementViewer = userRole === 'management_viewer';

    const canManage = isSuperAdmin || isHrManager || userPermissions.includes('ACCESS_PAYROLL');
    const canView = canManage || isManagementViewer || userPermissions.includes('VIEW_PAYROLL');

    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [selectedRunId, setSelectedRunId] = useState('');
    const [errorMsg, setErrorMsg] = useState(null);
    const [isForbidden, setIsForbidden] = useState(false);

    const fetchDashboard = useCallback(async (runIdToFetch = null) => {
        setLoading(true);
        setErrorMsg(null);
        setIsForbidden(false);
        try {
            const token = localStorage.getItem('token');
            const params = {};
            if (runIdToFetch) {
                params.runId = runIdToFetch;
            }
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/dashboard`, {
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'X-Tenant': tenant
                },
                withCredentials: true
            });

            if (res.data?.success && res.data?.data) {
                setDashboardData(res.data.data);
                if (res.data.data.selected_run?.id) {
                    setSelectedRunId(res.data.data.selected_run.id);
                }
            } else {
                setDashboardData(null);
            }
        } catch (err) {
            console.error('Failed to load payroll dashboard:', err);
            if (err.response?.status === 403) {
                setIsForbidden(true);
            }
            setErrorMsg(err.response?.data?.message || 'Could not load payroll dashboard data');
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const handleRunChange = (e) => {
        const newRunId = e.target.value;
        setSelectedRunId(newRunId);
        fetchDashboard(newRunId);
    };

    const kpis = dashboardData?.kpis;
    const selectedRun = dashboardData?.selected_run;
    const availableRuns = dashboardData?.available_runs || [];
    const trends = dashboardData?.trends || [];
    const departments = dashboardData?.departments || [];
    const recentRuns = dashboardData?.recent_runs || [];

    // Trend chart custom tooltip
    const renderTrendTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Paper
                    elevation={4}
                    sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#0f172a',
                        color: '#ffffff',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#93c5fd', mb: 0.5 }}>
                        {label}
                    </Typography>
                    {payload.map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: '0.8rem', my: 0.25 }}>
                            <span style={{ color: item.color }}>{item.name}:</span>
                            <span style={{ fontWeight: 700 }}>{money(item.value)} MMK</span>
                        </Box>
                    ))}
                    {payload[0]?.payload?.staff_count && (
                        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', mt: 0.75, pt: 0.5, fontSize: '0.75rem', color: '#94a3b8' }}>
                            Headcount: <strong>{payload[0].payload.staff_count} staff</strong>
                        </Box>
                    )}
                </Paper>
            );
        }
        return null;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: C.bg }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: { xs: 2, md: 3.5 }, maxWidth: 1600, width: '100%', mx: 'auto' }}>
                {/* Header Row */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2,
                        mb: 3
                    }}
                >
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    bgcolor: C.primaryLight,
                                    color: C.primary,
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(67, 56, 202, 0.12)'
                                }}
                            >
                                <PayrollIcon sx={{ fontSize: 26 }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: C.slateDark, letterSpacing: '-0.5px' }}>
                                    Payroll Dashboard
                                </Typography>
                                <Typography variant="caption" sx={{ color: C.slateMid, fontWeight: 500 }}>
                                    Executive cost analytics, compliance statutory summaries, and monthly execution tracker
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Period Switcher & Quick Action Buttons */}
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                        {availableRuns.length > 0 && (
                            <FormControl size="small" sx={{ minWidth: 200, bgcolor: '#ffffff' }}>
                                <Select
                                    value={selectedRunId}
                                    onChange={handleRunChange}
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border }
                                    }}
                                >
                                    {availableRuns.map(run => (
                                        <MenuItem key={run.id} value={run.id} sx={{ fontSize: '0.85rem' }}>
                                            <strong>{run.label}</strong>
                                            <Chip
                                                size="small"
                                                label={run.status}
                                                sx={{
                                                    ml: 1,
                                                    height: 18,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 800,
                                                    bgcolor: getStatusChipProps(run.status).bg,
                                                    color: getStatusChipProps(run.status).text
                                                }}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <Tooltip title="Refresh Dashboard">
                            <IconButton
                                onClick={() => fetchDashboard(selectedRunId)}
                                sx={{ bgcolor: '#ffffff', border: `1px solid ${C.border}`, borderRadius: 2 }}
                            >
                                <RefreshIcon fontSize="small" sx={{ color: C.slateMid }} />
                            </IconButton>
                        </Tooltip>

                        {canManage ? (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<ReviewIcon />}
                                    onClick={() => navigate('/payroll/draft-review')}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        borderColor: C.border,
                                        color: C.slateDark,
                                        bgcolor: '#ffffff',
                                        '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
                                    }}
                                >
                                    Review & Approval
                                </Button>

                                <Button
                                    variant="contained"
                                    startIcon={<RunIcon />}
                                    onClick={() => navigate('/payroll/execution')}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        bgcolor: C.primary,
                                        '&:hover': { bgcolor: '#3730a3' }
                                    }}
                                >
                                    Execute Run
                                </Button>
                            </>
                        ) : (
                            <Chip
                                icon={<SecurityIcon sx={{ fontSize: 16 }} />}
                                label="Executive View (Read-Only)"
                                sx={{
                                    bgcolor: '#f1f5f9',
                                    color: C.slateDark,
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    border: `1px solid ${C.border}`,
                                    py: 2,
                                    px: 0.5
                                }}
                            />
                        )}
                    </Stack>
                </Box>

                {(isForbidden || (!loading && !canView && storedUser)) && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            borderRadius: 3,
                            border: `1.5px solid ${C.border}`,
                            bgcolor: '#ffffff',
                            maxWidth: 540,
                            mx: 'auto',
                            my: 6
                        }}
                    >
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                bgcolor: '#fee2e2',
                                color: '#dc2626',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2
                            }}
                        >
                            <LockIcon sx={{ fontSize: 32 }} />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: C.slateDark, mb: 1 }}>
                            Access Restricted
                        </Typography>
                        <Typography variant="body2" sx={{ color: C.slateMid, mb: 3 }}>
                            You do not have permission to view the company-wide Payroll Dashboard. This area is restricted to HR Management and Executive Viewers. Please contact your system administrator in Security & Access Management if you require access.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/')}
                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: C.primary }}
                        >
                            Return to Home Dashboard
                        </Button>
                    </Paper>
                )}

                {errorMsg && !isForbidden && (
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                        {errorMsg}
                    </Alert>
                )}

                {loading && !dashboardData ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                        <CircularProgress size={40} sx={{ color: C.primary, mb: 2 }} />
                        <Typography variant="body2" sx={{ color: C.slateMid, fontWeight: 600 }}>
                            Aggregating payroll statistics...
                        </Typography>
                    </Box>
                ) : !dashboardData?.has_data ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            borderRadius: 3,
                            border: `1px solid ${C.border}`,
                            bgcolor: '#ffffff'
                        }}
                    >
                        <PayrollIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 800, color: C.slateDark, mb: 0.5 }}>
                            No Finalized or Active Payroll Runs Yet
                        </Typography>
                        <Typography variant="body2" sx={{ color: C.slateLight, maxWidth: 500, mx: 'auto', mb: 3 }}>
                            Generate your first draft payroll run in the Payroll Execution module to see executive dashboards, cost breakdowns, and trends.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<RunIcon />}
                            onClick={() => navigate('/payroll/execution')}
                            sx={{ borderRadius: 2, bgcolor: C.primary, textTransform: 'none', fontWeight: 700 }}
                        >
                            Go to Payroll Execution
                        </Button>
                    </Paper>
                ) : (
                    <>
                        {/* Current Cycle Progress & Workflow State Banner */}
                        {selectedRun && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    mb: 3,
                                    borderRadius: 3,
                                    border: `1.5px solid ${C.border}`,
                                    bgcolor: '#ffffff',
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                    gap: 2
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box
                                        sx={{
                                            bgcolor: getStatusChipProps(selectedRun.status).bg,
                                            color: getStatusChipProps(selectedRun.status).text,
                                            px: 1.5,
                                            py: 0.75,
                                            borderRadius: 2,
                                            fontWeight: 800,
                                            fontSize: '0.8rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5
                                        }}
                                    >
                                        {selectedRun.status}
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: C.slateDark, lineHeight: 1.2 }}>
                                            {selectedRun.period_label} — {selectedRun.run_type} Payroll
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: C.slateMid }}>
                                            Group: <strong>{selectedRun.payroll_group_name || 'Standard'}</strong> &bull; Run Date:{' '}
                                            {selectedRun.run_date ? new Date(selectedRun.run_date).toLocaleDateString() : 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Stepper Progress indicators */}
                                <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleIcon sx={{ fontSize: 18, color: C.emerald }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateDark }}>
                                            Attendance Synced
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleIcon sx={{ fontSize: 18, color: C.emerald }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateDark }}>
                                            Calculation Complete
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleIcon
                                            sx={{
                                                fontSize: 18,
                                                color: ['APPROVED', 'FINALIZED'].includes(selectedRun.status) ? C.emerald : '#cbd5e1'
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 700,
                                                color: ['APPROVED', 'FINALIZED'].includes(selectedRun.status) ? C.slateDark : '#94a3b8'
                                            }}
                                        >
                                            Approval Passed
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleIcon
                                            sx={{
                                                fontSize: 18,
                                                color: selectedRun.status === 'FINALIZED' ? C.emerald : '#cbd5e1'
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 700,
                                                color: selectedRun.status === 'FINALIZED' ? C.slateDark : '#94a3b8'
                                            }}
                                        >
                                            Finalized & Paid
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        )}

                        {/* Executive KPI Cards (Row 1) - Responsive 5-column CSS Grid */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, 1fr)',
                                    md: 'repeat(3, 1fr)',
                                    lg: 'repeat(5, 1fr)'
                                },
                                gap: 2.5,
                                mb: 3
                            }}
                        >
                            {/* Card 1: Total Gross Payroll */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    border: `1.5px solid ${C.border}`,
                                    bgcolor: '#ffffff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)' }
                                }}
                            >
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Total Gross Pay
                                        </Typography>
                                        <Box sx={{ p: 0.75, borderRadius: 2, bgcolor: '#eff6ff', color: '#1d4ed8' }}>
                                            <PayrollIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: C.slateDark, letterSpacing: '-0.5px' }}>
                                        {money(kpis?.gross_pay)}
                                        <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 700, color: C.slateLight }}>
                                            MMK
                                        </Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {kpis?.mom?.gross_pct !== 0 && (
                                        <Chip
                                            size="small"
                                            icon={kpis?.mom?.gross_pct > 0 ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                                            label={`${kpis?.mom?.gross_pct > 0 ? '+' : ''}${kpis?.mom?.gross_pct}%`}
                                            sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                fontWeight: 800,
                                                bgcolor: kpis?.mom?.gross_pct > 0 ? C.roseLight : C.emeraldLight,
                                                color: kpis?.mom?.gross_pct > 0 ? C.rose : C.emerald
                                            }}
                                        />
                                    )}
                                    <Typography variant="caption" sx={{ color: C.slateLight, fontSize: '0.72rem' }}>
                                        Avg {money(kpis?.avg_gross)} / employee
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* Card 2: Net Take-Home Pay */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    border: `1.5px solid ${C.border}`,
                                    bgcolor: '#ffffff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)' }
                                }}
                            >
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Net Take-Home
                                        </Typography>
                                        <Box sx={{ p: 0.75, borderRadius: 2, bgcolor: C.emeraldLight, color: C.emerald }}>
                                            <PayslipIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#047857', letterSpacing: '-0.5px' }}>
                                        {money(kpis?.net_pay)}
                                        <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 700, color: C.slateLight }}>
                                            MMK
                                        </Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: C.slateLight, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                                        {kpis?.gross_pay > 0 ? ((kpis.net_pay / kpis.gross_pay) * 100).toFixed(1) : 0}% of Gross
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: C.slateLight, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                                        Avg {money(kpis?.avg_net)}
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* Card 3: Company Payroll Cost (CTC) */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    border: `1.5px solid ${C.border}`,
                                    bgcolor: '#ffffff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)' }
                                }}
                            >
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Total Company CTC
                                        </Typography>
                                        <Box sx={{ p: 0.75, borderRadius: 2, bgcolor: '#f5f3ff', color: '#6d28d9' }}>
                                            <CostCentreIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: C.slateDark, letterSpacing: '-0.5px' }}>
                                        {money(kpis?.employer_cost)}
                                        <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 700, color: C.slateLight }}>
                                            MMK
                                        </Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="caption" sx={{ color: C.slateLight, fontSize: '0.72rem' }}>
                                        Includes {money(kpis?.ssb_employer)} MMK employer SSB
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* Card 4: Total Withholdings & Deductions */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    border: `1.5px solid ${C.border}`,
                                    bgcolor: '#ffffff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)' }
                                }}
                            >
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Withholdings & Tax
                                        </Typography>
                                        <Box sx={{ p: 0.75, borderRadius: 2, bgcolor: C.roseLight, color: C.rose }}>
                                            <TaxIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: C.rose, letterSpacing: '-0.5px' }}>
                                        {money(kpis?.total_deductions)}
                                        <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 700, color: C.slateLight }}>
                                            MMK
                                        </Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: C.slateLight, fontSize: '0.72rem' }}>
                                        Tax: <strong>{money(kpis?.tax_deduction)}</strong> &bull; SSB: <strong>{money(kpis?.ssb_employee)}</strong>
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* Card 5: Headcount & Distribution Status */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    border: `1.5px solid ${C.border}`,
                                    bgcolor: '#ffffff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)' }
                                }}
                            >
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Processed Staff
                                        </Typography>
                                        <Box sx={{ p: 0.75, borderRadius: 2, bgcolor: C.amberLight, color: C.amber }}>
                                            <HeadcountIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: C.slateDark, letterSpacing: '-0.5px' }}>
                                        {kpis?.staff_count || 0}
                                        <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 700, color: C.slateLight }}>
                                            Employees
                                        </Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" sx={{ color: C.slateLight, fontSize: '0.72rem' }}>
                                        Released: <strong>{kpis?.released_count || 0}</strong> / {kpis?.staff_count || 0}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={kpis?.released_count === kpis?.staff_count && kpis?.staff_count > 0 ? '100% Out' : `${kpis?.released_count || 0} Sent`}
                                        sx={{
                                            height: 18,
                                            fontSize: '0.65rem',
                                            fontWeight: 800,
                                            bgcolor: kpis?.released_count === kpis?.staff_count && kpis?.staff_count > 0 ? C.emeraldLight : '#f1f5f9',
                                            color: kpis?.released_count === kpis?.staff_count && kpis?.staff_count > 0 ? C.emerald : C.slateMid
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Box>

                        {/* Middle Row: Trend Chart & Department Cost Distribution */}
                        <Grid container spacing={2.5} sx={{ mt: 0, mb: 3 }}>
                            {/* Monthly Cost Trend (Area Chart) */}
                            <Grid item xs={12} lg={7.5} sx={{ display: 'flex' }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        border: `1.5px solid ${C.border}`,
                                        bgcolor: '#ffffff',
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                Payroll Cost Trends
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: C.slateLight }}>
                                                Historical Gross Pay, Net Take-Home, and Company CTC across recent cycles
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {trends.length === 0 ? (
                                        <Box sx={{ py: 8, textAlign: 'center', color: C.slateLight }}>
                                            No trend data available
                                        </Box>
                                    ) : (
                                        <Box sx={{ width: '100%', height: 320, mt: 1 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={trends} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                                    <defs>
                                                        <linearGradient id="ctcGrad" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor={C.primary} stopOpacity={0.25} />
                                                            <stop offset="95%" stopColor={C.primary} stopOpacity={0.0} />
                                                        </linearGradient>
                                                        <linearGradient id="grossGrad" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor={C.secondary} stopOpacity={0.25} />
                                                            <stop offset="95%" stopColor={C.secondary} stopOpacity={0.0} />
                                                        </linearGradient>
                                                        <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor={C.emerald} stopOpacity={0.25} />
                                                            <stop offset="95%" stopColor={C.emerald} stopOpacity={0.0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                                    <XAxis
                                                        dataKey="label"
                                                        tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                                                        axisLine={{ stroke: '#e2e8f0' }}
                                                        tickLine={false}
                                                    />
                                                    <YAxis
                                                        tickFormatter={formatCompact}
                                                        tick={{ fill: '#64748b', fontSize: 12 }}
                                                        axisLine={false}
                                                        tickLine={false}
                                                    />
                                                    <ChartTooltip content={renderTrendTooltip} />
                                                    <Legend
                                                        wrapperStyle={{ paddingTop: 10, fontSize: '0.8rem', fontWeight: 600 }}
                                                        iconType="circle"
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="employer_cost"
                                                        name="Company CTC"
                                                        stroke={C.primary}
                                                        strokeWidth={2.5}
                                                        fillOpacity={1}
                                                        fill="url(#ctcGrad)"
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="gross_pay"
                                                        name="Gross Pay"
                                                        stroke={C.secondary}
                                                        strokeWidth={2}
                                                        fillOpacity={1}
                                                        fill="url(#grossGrad)"
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="net_pay"
                                                        name="Net Take-Home"
                                                        stroke={C.emerald}
                                                        strokeWidth={2}
                                                        fillOpacity={1}
                                                        fill="url(#netGrad)"
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>

                            {/* Department Cost Distribution */}
                            <Grid item xs={12} lg={4.5} sx={{ display: 'flex' }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        border: `1.5px solid ${C.border}`,
                                        bgcolor: '#ffffff',
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                Cost by Department
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: C.slateLight }}>
                                                Department expenditure share for {selectedRun?.period_label || 'current cycle'}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            size="small"
                                            label={`${departments.length} Units`}
                                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 800, bgcolor: '#f1f5f9', color: C.slateMid }}
                                        />
                                    </Box>

                                    {departments.length === 0 ? (
                                        <Box sx={{ py: 6, textAlign: 'center', color: C.slateLight }}>
                                            No department breakdown available
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, overflowY: 'auto', maxHeight: 320, pr: 0.5 }}>
                                            {departments.map((dept, idx) => (
                                                <Box key={idx} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #f1f5f9' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <DeptIcon sx={{ fontSize: 16, color: C.primary }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 700, color: C.slateDark }}>
                                                                {dept.dept_name}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                            {money(dept.gross_pay)} MMK
                                                        </Typography>
                                                    </Box>

                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(100, dept.share_pct)}
                                                        sx={{
                                                            height: 6,
                                                            borderRadius: 3,
                                                            bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor: idx === 0 ? C.primary : idx === 1 ? C.secondary : C.emerald,
                                                                borderRadius: 3
                                                            }
                                                        }}
                                                    />

                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
                                                        <Typography variant="caption" sx={{ color: C.slateLight, fontSize: '0.7rem' }}>
                                                            {dept.staff_count} Employee{dept.staff_count > 1 ? 's' : ''}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateMid, fontSize: '0.7rem' }}>
                                                            {dept.share_pct}% of budget
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Bottom Row 1: Earnings Composition & Deductions Composition */}
                        <Grid container spacing={2.5} sx={{ mt: 0, mb: 3 }}>
                            {/* Earnings Breakdown */}
                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        border: `1.5px solid ${C.border}`,
                                        bgcolor: '#ffffff'
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.slateDark, mb: 0.5 }}>
                                        Earnings Composition
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: C.slateLight, display: 'block', mb: 2 }}>
                                        Breakdown of basic salary, overtime, and allowances
                                    </Typography>

                                    <Stack spacing={2}>
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateMid }}>Basic Salary</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                    {money(kpis?.basic_salary)} MMK ({kpis?.gross_pay > 0 ? ((kpis.basic_salary / kpis.gross_pay) * 100).toFixed(1) : 0}%)
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={kpis?.gross_pay > 0 ? (kpis.basic_salary / kpis.gross_pay) * 100 : 0}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }}
                                            />
                                        </Box>

                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateMid }}>Allowances & Benefits</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                    {money(kpis?.allowances)} MMK ({kpis?.gross_pay > 0 ? ((kpis.allowances / kpis.gross_pay) * 100).toFixed(1) : 0}%)
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={kpis?.gross_pay > 0 ? (kpis.allowances / kpis.gross_pay) * 100 : 0}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#10b981' } }}
                                            />
                                        </Box>

                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateMid }}>Overtime Pay (OT)</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                    {money(kpis?.ot_pay)} MMK ({kpis?.gross_pay > 0 ? ((kpis.ot_pay / kpis.gross_pay) * 100).toFixed(1) : 0}%)
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={kpis?.gross_pay > 0 ? (kpis.ot_pay / kpis.gross_pay) * 100 : 0}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b' } }}
                                            />
                                        </Box>

                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateMid }}>Other Additions</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                    {money(kpis?.other_earnings)} MMK ({kpis?.gross_pay > 0 ? ((kpis.other_earnings / kpis.gross_pay) * 100).toFixed(1) : 0}%)
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={kpis?.gross_pay > 0 ? (kpis.other_earnings / kpis.gross_pay) * 100 : 0}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#8b5cf6' } }}
                                            />
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>

                            {/* Deductions Breakdown */}
                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        border: `1.5px solid ${C.border}`,
                                        bgcolor: '#ffffff'
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.slateDark, mb: 0.5 }}>
                                        Deductions & Statutory Withholdings
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: C.slateLight, display: 'block', mb: 2 }}>
                                        Breakdown of income taxes, social security, and attendance penalties
                                    </Typography>

                                    <Stack spacing={2}>
                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateMid }}>Income Tax (PIT)</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                    {money(kpis?.tax_deduction)} MMK ({kpis?.total_deductions > 0 ? ((kpis.tax_deduction / kpis.total_deductions) * 100).toFixed(1) : 0}%)
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={kpis?.total_deductions > 0 ? (kpis.tax_deduction / kpis.total_deductions) * 100 : 0}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#f43f5e' } }}
                                            />
                                        </Box>

                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateMid }}>Social Security (SSB 2%)</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                    {money(kpis?.ssb_employee)} MMK ({kpis?.total_deductions > 0 ? ((kpis.ssb_employee / kpis.total_deductions) * 100).toFixed(1) : 0}%)
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={kpis?.total_deductions > 0 ? (kpis.ssb_employee / kpis.total_deductions) * 100 : 0}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#ea580c' } }}
                                            />
                                        </Box>

                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateMid }}>Unpaid Leave & Absent Deductions</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                    {money(kpis?.unpaid_penalties)} MMK ({kpis?.total_deductions > 0 ? ((kpis.unpaid_penalties / kpis.total_deductions) * 100).toFixed(1) : 0}%)
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={kpis?.total_deductions > 0 ? (kpis.unpaid_penalties / kpis.total_deductions) * 100 : 0}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#dc2626' } }}
                                            />
                                        </Box>

                                        <Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: C.slateMid }}>Other Deductions & Adjustments</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: C.slateDark }}>
                                                    {money(kpis?.other_deductions)} MMK ({kpis?.total_deductions > 0 ? ((kpis.other_deductions / kpis.total_deductions) * 100).toFixed(1) : 0}%)
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={kpis?.total_deductions > 0 ? (kpis.other_deductions / kpis.total_deductions) * 100 : 0}
                                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#64748b' } }}
                                            />
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Bottom Row 2: Recent Payroll Cycles Table */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: `1.5px solid ${C.border}`,
                                bgcolor: '#ffffff'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.slateDark }}>
                                        Recent Payroll Cycles
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: C.slateLight }}>
                                        Status summary of latest payroll runs and direct workflow shortcuts
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => navigate('/payroll/draft-review')}
                                    sx={{ textTransform: 'none', fontWeight: 700, color: C.primary }}
                                >
                                    View All Runs
                                </Button>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            <TableCell sx={{ fontWeight: 800, color: C.slateMid, fontSize: '0.75rem', py: 1.5 }}>PERIOD & TYPE</TableCell>
                                            <TableCell sx={{ fontWeight: 800, color: C.slateMid, fontSize: '0.75rem', py: 1.5 }}>GROUP</TableCell>
                                            <TableCell sx={{ fontWeight: 800, color: C.slateMid, fontSize: '0.75rem', py: 1.5 }}>RUN DATE</TableCell>
                                            <TableCell sx={{ fontWeight: 800, color: C.slateMid, fontSize: '0.75rem', py: 1.5 }}>STAFF COUNT</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: C.slateMid, fontSize: '0.75rem', py: 1.5 }}>TOTAL GROSS</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: C.slateMid, fontSize: '0.75rem', py: 1.5 }}>NET PAYABLE</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 800, color: C.slateMid, fontSize: '0.75rem', py: 1.5 }}>STATUS</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: C.slateMid, fontSize: '0.75rem', py: 1.5 }}>ACTION</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {recentRuns.map(r => (
                                            <TableRow key={r.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell sx={{ fontWeight: 700, color: C.slateDark, fontSize: '0.85rem' }}>
                                                    {r.period_label}
                                                    <Typography variant="caption" sx={{ display: 'block', color: C.slateLight, fontSize: '0.7rem' }}>
                                                        {r.run_type}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.85rem', color: C.slateMid, fontWeight: 600 }}>
                                                    {r.group_name || 'Standard'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.85rem', color: C.slateLight }}>
                                                    {r.run_date ? new Date(r.run_date).toLocaleDateString() : 'N/A'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: C.slateDark }}>
                                                    {r.staff_count} staff
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700, color: C.slateDark }}>
                                                    {money(r.total_gross)} MMK
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 800, color: C.primary }}>
                                                    {money(r.total_net_pay)} MMK
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        size="small"
                                                        label={r.status}
                                                        sx={{
                                                            height: 22,
                                                            fontSize: '0.7rem',
                                                            fontWeight: 800,
                                                            bgcolor: getStatusChipProps(r.status).bg,
                                                            color: getStatusChipProps(r.status).text
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        {canManage ? (
                                                            <>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    onClick={() => navigate('/payroll/draft-review')}
                                                                    sx={{
                                                                        textTransform: 'none',
                                                                        fontSize: '0.72rem',
                                                                        fontWeight: 700,
                                                                        py: 0.25,
                                                                        px: 1,
                                                                        borderRadius: 1.5,
                                                                        borderColor: C.border,
                                                                        color: C.slateDark
                                                                    }}
                                                                >
                                                                    Review
                                                                </Button>
                                                                {r.status === 'FINALIZED' && (
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        onClick={() => navigate('/payroll/payslip-distribution')}
                                                                        sx={{
                                                                            textTransform: 'none',
                                                                            fontSize: '0.72rem',
                                                                            fontWeight: 700,
                                                                            py: 0.25,
                                                                            px: 1,
                                                                            borderRadius: 1.5,
                                                                            bgcolor: C.emerald,
                                                                            '&:hover': { bgcolor: '#059669' }
                                                                        }}
                                                                    >
                                                                        Payslips
                                                                    </Button>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Chip
                                                                size="small"
                                                                label="View Only"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.68rem',
                                                                    fontWeight: 700,
                                                                    borderColor: C.border,
                                                                    color: C.slateMid
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PayrollDashboard;
